import { useEffect } from "react";

const noDeps = [];

/**
 * Util to safely clear a video reference, just removing the reference is not enough, as the video will continue to load
 * in the background after the SPA page change.
 *
 * So by setting the src to an empty string and calling load, we ensure that the video is cleared from memory.
 */
export function useVideoCleanup(
  /**
   * For more convenience, we allow the ref to be either a single video element or an object of video elements (in case
   * of dynamic refs on tooltips)
   */
  ref:
    | React.MutableRefObject<HTMLVideoElement | null>
    | React.MutableRefObject<Record<string, HTMLVideoElement>>,
  deps: Array<unknown> = noDeps,
) {
  useEffect(() => {
    const videoEl = ref.current;

    return () => {
      if (videoEl) {
        try {
          if (videoEl instanceof HTMLVideoElement) {
            destroyVideo(videoEl);
          } else {
            Object.values(videoEl).forEach((el) => {
              if (el instanceof HTMLVideoElement) {
                destroyVideo(el);
              }
            });
          }
        } catch {
          console.error("Error during video cleanup:", videoEl);
        }
      }

      ref.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}

export const destroyVideo = (videoEl: HTMLVideoElement | null) => {
  if (!videoEl) {
    return;
  }

  try {
    videoEl.pause();
    videoEl.src = "";
    videoEl.load();
  } catch (error) {
    console.error("Error clearing video:", error);
  }
};
